import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';

import { ProjectName } from '#components/project-name/project-name.js';
import { useTRPC } from '#src/trpc.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';
import { CrumbPopover } from './crumb-popover.js';
import { usePopoverSearch } from './use-popover-search.js';

export function ProjectCrumb({
	orgName,
	projectId,
	revisionId,
}: {
	orgName: string;
	projectId: string;
	revisionId: string | undefined;
}) {
	const trpc = useTRPC();
	const navigate = useNavigate();
	const lastOpenProject = useAtomValue(lastOpenProjectAtom);
	const { anchorEl, isOpen, search, setSearch, handleOpen, handleClose } = usePopoverSearch();

	const { data: previewData } = useQuery(
		trpc.project.getPreview.queryOptions({ id: projectId }, { throwOnError: false }),
	);

	const { data: listData, isPending } = useQuery(
		trpc.project.list.queryOptions({ organizationName: orgName }, { enabled: isOpen, throwOnError: false }),
	);

	const projectName = previewData?.projectPreview.name;

	const handleSelect = (selectedProjectId: string) => {
		handleClose();
		const selectedProject = listData?.projectPreviews.find((p) => p.id === selectedProjectId);
		const selectedRevisionId = selectedProject?.latestRevision?.id;

		if (revisionId && selectedRevisionId) {
			void navigate({
				to: '.',
				params: (prev) => ({ ...prev, projectId: selectedProjectId, revisionId: selectedRevisionId }),
			});
		} else {
			void navigate({
				to: '/orgs/$orgName/projects/$projectId/overview',
				params: { orgName, projectId: selectedProjectId },
			});
		}
	};

	const filtered = (listData?.projectPreviews ?? [])
		.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
		.toSorted((a, b) => a.name.localeCompare(b.name));

	return (
		<>
			<div className='flex items-center'>
				<ProjectName isDemo={previewData?.projectPreview.isDemo ?? false} gapClassName='gap-1.5'>
					<Link
						to='/orgs/$orgName/projects/$projectId/overview'
						params={{ orgName, projectId }}
						className='py-0.5 text-sm text-gray-a10 transition-colors hover:text-gray-a12'
					>
						{projectName ?? <Skeleton width={80} />}
					</Link>
				</ProjectName>
				<IconButton
					size='small'
					onClick={handleOpen}
					disabled={!lastOpenProject.orgName}
					className='-mr-1.5 ml-0.5 p-0.5 text-gray-a9 hover:text-gray-a12'
				>
					<ArrowDropDownIcon className='text-base' />
				</IconButton>
			</div>

			<CrumbPopover
				anchorEl={anchorEl}
				open={isOpen}
				onClose={handleClose}
				heading='Projects'
				search={search}
				onSearchChange={setSearch}
				isPending={isPending}
				emptyLabel='No projects found'
				isEmpty={filtered.length === 0}
			>
				{filtered.map((project) => (
					<MenuItem key={project.id} selected={project.id === projectId} onClick={() => handleSelect(project.id)}>
						<ProjectName isDemo={project.isDemo}>
							<span>{project.name}</span>
						</ProjectName>
					</MenuItem>
				))}
			</CrumbPopover>
		</>
	);
}
